import axios from 'axios'

/*
 * Central axios instance — mirrors the proven behaviour of the legacy front:
 *  - base URL `/api/v1` (relative; proxied by Vite in dev, nginx in prod)
 *  - request: inject `Authorization: Bearer <access_token>` + `X-Company-Id`
 *  - response: 401 on auth endpoints -> logout+redirect; 403 ONBOARDING_INCOMPLETE
 *    -> redirect to pending-validation page.
 * Token + company id live in localStorage (same keys as legacy for continuity).
 */
export const TOKEN_KEY = 'access_token'
export const REFRESH_KEY = 'refresh_token'
export const COMPANY_KEY = 'current_company_id'

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) config.headers.Authorization = `Bearer ${token}`
  const companyId = localStorage.getItem(COMPANY_KEY)
  if (companyId) config.headers['X-Company-Id'] = companyId
  return config
})

const AUTH_LOGOUT_PATHS = ['/auth/profile', '/auth/refresh', '/auth/me']

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status
    const url = error?.config?.url || ''
    const code = error?.response?.data?.code || error?.response?.data?.error

    if (status === 401 && AUTH_LOGOUT_PATHS.some((p) => url.includes(p))) {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_KEY)
      if (window.location.pathname !== '/login') window.location.assign('/login')
    }

    // A 403 ONBOARDING_INCOMPLETE means the user's registration isn't finished.
    // We let /auth/login and /register/* handlers route explicitly (they read the
    // tokens + onboarding step from the body); only auto-redirect for OTHER calls
    // so an authenticated app session that hits it lands on the waiting screen.
    const handledByCaller = url.includes('/auth/login') || url.includes('/register')
    if (status === 403 && code === 'ONBOARDING_INCOMPLETE' && !handledByCaller) {
      if (!window.location.pathname.includes('company-pending-validation') && !window.location.pathname.includes('/register')) {
        window.location.assign('/app/company-pending-validation')
      }
    }

    return Promise.reject(error)
  },
)

export default api
